/**
 * X-Frame Companion App - Compound Seed Data
 * 
 * Pharmacokinetic data sourced from SteroidPlotter dataset
 * with academic citations for each compound.
 * 
 * Data includes half-life, Cmax, Tmax, and bioavailability
 * for accurate blood level curve modeling.
 */

export type CompoundCategory = 'steroid' | 'peptide' | 'ancillary' | 'sarm';
export type CompoundClass = 'testosterone' | 'dht_derivative' | '19_nor' | 'oral_steroid' | 'peptide' | 'ai' | 'serm' | 'other';
export type PKModelType = 'advanced' | 'basic';

export interface CompoundSeedData {
  name: string;
  shortName: string;
  ester: string | null;
  category: CompoundCategory;
  compoundClass: CompoundClass;
  
  // Pharmacokinetic parameters
  halfLifeDays: number;
  halfLifeHours: number;
  cmax: number | null;          // Peak concentration coefficient
  tmaxDays: number | null;      // Time to peak (days)
  bioavailability: number | null; // Fraction absorbed (0-1)
  pkModel: PKModelType;
  
  // Compound properties for interaction analysis
  aromatizes: boolean;
  aromatizationRate: 'none' | 'low' | 'moderate' | 'high';
  dhtConversion: boolean;
  is19Nor: boolean;
  hepatotoxicity: 'none' | 'low' | 'moderate' | 'high';
  
  // Dosing guidance
  typicalDoseRangeMg: string;
  recommendedFrequency: string;
  
  // Administration
  route: 'injectable' | 'oral' | 'topical' | 'sublingual';
  
  // Sources
  sources: string[];
}

export const COMPOUND_SEED_DATA: CompoundSeedData[] = [
  // ============================================
  // TESTOSTERONE ESTERS
  // ============================================
  {
    name: 'Testosterone Enanthate',
    shortName: 'Test E',
    ester: 'Enanthate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 7.19,
    halfLifeHours: 172.56,
    cmax: 11.3095,
    tmaxDays: 1.3875,
    bioavailability: 0.72,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '200-600',
    recommendedFrequency: '2x/week',
    route: 'injectable',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4721027/',
      'https://journals.physiology.org/doi/full/10.1152/ajpendo.00502.2001'
    ]
  },
  {
    name: 'Testosterone Cypionate',
    shortName: 'Test C',
    ester: 'Cypionate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 6.9,
    halfLifeHours: 165.6,
    cmax: 5.56,
    tmaxDays: 4.5,
    bioavailability: 0.70,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '200-600',
    recommendedFrequency: '2x/week',
    route: 'injectable',
    sources: [
      'https://tau.amegroups.org/article/view/11328/13164',
      'https://ascpt.onlinelibrary.wiley.com/doi/full/10.1002/psp4.12287'
    ]
  },
  {
    name: 'Testosterone Propionate',
    shortName: 'Test P',
    ester: 'Propionate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 1.04,
    halfLifeHours: 24.9,
    cmax: 26.0,
    tmaxDays: 1.0625,
    bioavailability: 0.84,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '50-150',
    recommendedFrequency: 'daily',
    route: 'injectable',
    sources: [
      'https://academic.oup.com/jcem/article-abstract/63/6/1361/2674622'
    ]
  },
  {
    name: 'Testosterone Phenylpropionate',
    shortName: 'Test PP',
    ester: 'Phenylpropionate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 2.5,
    halfLifeHours: 60,
    cmax: 14.5, // Estimated based on ester chain length interpolation
    tmaxDays: 0.8,
    bioavailability: 0.69,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '50-100',
    recommendedFrequency: 'eod',
    route: 'injectable',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9611952/'
    ]
  },
  {
    name: 'Testosterone Isocaproate',
    shortName: 'Test Iso',
    ester: 'Isocaproate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 3.1,
    halfLifeHours: 74.4,
    cmax: 12.5, // Estimated based on ester chain length interpolation
    tmaxDays: 1.0,
    bioavailability: 0.75,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '50-100',
    recommendedFrequency: 'eod',
    route: 'injectable',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9611952/'
    ]
  },
  {
    name: 'Testosterone Decanoate',
    shortName: 'Test D',
    ester: 'Decanoate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 5.6,
    halfLifeHours: 134.4,
    cmax: 8.5, // Estimated based on ester chain length (similar to cypionate)
    tmaxDays: 2.5,
    bioavailability: 0.65,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '100-200',
    recommendedFrequency: '1x/week',
    route: 'injectable',
    sources: []
  },
  {
    name: 'Testosterone Undecanoate (Nebido)',
    shortName: 'Test U',
    ester: 'Undecanoate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 33.9,
    halfLifeHours: 813.6,
    cmax: 1.187,
    tmaxDays: 10.0,
    bioavailability: 0.65,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '750-1000',
    recommendedFrequency: 'every 10-14 days',
    route: 'injectable',
    sources: [
      'https://onlinelibrary.wiley.com/doi/full/10.2164/jandrol.109.009597',
      'https://www.bayer.com/sites/default/files/NEBIDO_EN_PI.pdf'
    ]
  },
  {
    name: 'Testosterone Suspension',
    shortName: 'Test Susp',
    ester: null,
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 1.375,
    halfLifeHours: 33,
    cmax: 5.067,
    tmaxDays: 0.25,
    bioavailability: 1.0,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'high',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '25-100',
    recommendedFrequency: 'daily',
    route: 'injectable',
    sources: []
  },

  // ============================================
  // NANDROLONE (19-NOR)
  // ============================================
  {
    name: 'Nandrolone Phenylpropionate',
    shortName: 'NPP',
    ester: 'Phenylpropionate',
    category: 'steroid',
    compoundClass: '19_nor',
    halfLifeDays: 2.4,
    halfLifeHours: 57.6,
    cmax: 8.91465,
    tmaxDays: 1.0,
    bioavailability: 0.67,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'low',
    dhtConversion: false, // Converts to DHN instead
    is19Nor: true,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '200-400',
    recommendedFrequency: 'eod',
    route: 'injectable',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/9103484/'
    ]
  },
  {
    name: 'Nandrolone Decanoate',
    shortName: 'Deca',
    ester: 'Decanoate',
    category: 'steroid',
    compoundClass: '19_nor',
    halfLifeDays: 10.2,
    halfLifeHours: 244.8,
    cmax: 3.99,
    tmaxDays: 1.833,
    bioavailability: 0.73,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'low',
    dhtConversion: false,
    is19Nor: true,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '200-600',
    recommendedFrequency: '1x/week',
    route: 'injectable',
    sources: [
      'https://academic.oup.com/jcem/article/90/5/2624/2836761'
    ]
  },

  // ============================================
  // TRENBOLONE (19-NOR)
  // ============================================
  {
    name: 'Trenbolone Acetate',
    shortName: 'Tren A',
    ester: 'Acetate',
    category: 'steroid',
    compoundClass: '19_nor',
    halfLifeDays: 1.5,
    halfLifeHours: 36,
    cmax: 28.0, // Estimated - trenbolone is ~3-5x more potent than testosterone
    tmaxDays: 0.5,
    bioavailability: 0.87,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: true,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '200-400',
    recommendedFrequency: 'daily',
    route: 'injectable',
    sources: [
      'https://www.sciencedirect.com/science/article/abs/pii/S002228602030452X'
    ]
  },
  {
    name: 'Trenbolone Enanthate',
    shortName: 'Tren E',
    ester: 'Enanthate',
    category: 'steroid',
    compoundClass: '19_nor',
    halfLifeDays: 11.0,
    halfLifeHours: 264,
    cmax: 12.0, // Estimated based on enanthate ester release profile
    tmaxDays: 2.0,
    bioavailability: 0.71,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: true,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '200-400',
    recommendedFrequency: '2x/week',
    route: 'injectable',
    sources: [
      'https://www.sciencedirect.com/science/article/abs/pii/S002228602030452X'
    ]
  },
  {
    name: 'Trenbolone Hexahydrobenzylcarbonate',
    shortName: 'Parabolan',
    ester: 'Hexahydrobenzylcarbonate',
    category: 'steroid',
    compoundClass: '19_nor',
    halfLifeDays: 8.0,
    halfLifeHours: 192,
    cmax: 14.0, // Estimated based on ester release profile
    tmaxDays: 1.5,
    bioavailability: 0.66,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: true,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '150-300',
    recommendedFrequency: '2x/week',
    route: 'injectable',
    sources: [
      'https://www.sciencedirect.com/science/article/abs/pii/S002228602030452X'
    ]
  },

  // ============================================
  // DHT DERIVATIVES
  // ============================================
  {
    name: 'Drostanolone Propionate',
    shortName: 'Mast P',
    ester: 'Propionate',
    category: 'steroid',
    compoundClass: 'dht_derivative',
    halfLifeDays: 2.0,
    halfLifeHours: 48,
    cmax: 18.0, // Estimated based on propionate ester
    tmaxDays: 0.7,
    bioavailability: 0.84,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false, // Already DHT derivative
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '200-400',
    recommendedFrequency: 'eod',
    route: 'injectable',
    sources: []
  },
  {
    name: 'Drostanolone Enanthate',
    shortName: 'Mast E',
    ester: 'Enanthate',
    category: 'steroid',
    compoundClass: 'dht_derivative',
    halfLifeDays: 4.5,
    halfLifeHours: 108,
    cmax: 10.0, // Estimated based on enanthate ester
    tmaxDays: 1.5,
    bioavailability: 0.73,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '200-400',
    recommendedFrequency: '2x/week',
    route: 'injectable',
    sources: []
  },
  {
    name: 'Methenolone Enanthate',
    shortName: 'Primo',
    ester: 'Enanthate',
    category: 'steroid',
    compoundClass: 'dht_derivative',
    halfLifeDays: 10.5,
    halfLifeHours: 252,
    cmax: 32.0, // Based on SteroidPlotter data - primo shows high ng/dL per mg
    tmaxDays: 2.0,
    bioavailability: 0.73,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '400-800',
    recommendedFrequency: '2x/week',
    route: 'injectable',
    sources: []
  },
  {
    name: 'Mesterolone',
    shortName: 'Proviron',
    ester: null,
    category: 'steroid',
    compoundClass: 'dht_derivative',
    halfLifeDays: 0.52,
    halfLifeHours: 12.5,
    cmax: 12.4,
    tmaxDays: 0.067,
    bioavailability: 0.03,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '25-75',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: [
      'https://resources.bayer.com.au/resources/uploads/PI/file9420.pdf'
    ]
  },

  // ============================================
  // OTHER INJECTABLES
  // ============================================
  {
    name: 'Boldenone Undecylenate',
    shortName: 'EQ',
    ester: 'Undecylenate',
    category: 'steroid',
    compoundClass: 'testosterone',
    halfLifeDays: 5.125,
    halfLifeHours: 123,
    cmax: 1.098,
    tmaxDays: 6.0,
    bioavailability: 0.63,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'low',
    dhtConversion: true,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '400-800',
    recommendedFrequency: '1x/week',
    route: 'injectable',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/17348894/'
    ]
  },

  // ============================================
  // ORAL STEROIDS
  // ============================================
  {
    name: 'Oxandrolone',
    shortName: 'Anavar',
    ester: null,
    category: 'steroid',
    compoundClass: 'oral_steroid',
    halfLifeDays: 0.28,
    halfLifeHours: 6.7,
    cmax: 772.0,
    tmaxDays: 0.067,
    bioavailability: 0.625,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '20-80',
    recommendedFrequency: '2x/day',
    route: 'oral',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7134583/'
    ]
  },
  {
    name: 'Oxymetholone',
    shortName: 'Anadrol',
    ester: null,
    category: 'steroid',
    compoundClass: 'oral_steroid',
    halfLifeDays: 0.33,
    halfLifeHours: 8.0,
    cmax: 37.6,
    tmaxDays: 0.146,
    bioavailability: 0.95,
    pkModel: 'advanced',
    aromatizes: false, // But has estrogenic activity
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'high',
    typicalDoseRangeMg: '50-100',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: []
  },
  {
    name: 'Methandrostenolone',
    shortName: 'Dianabol',
    ester: null,
    category: 'steroid',
    compoundClass: 'oral_steroid',
    halfLifeDays: 0.22,
    halfLifeHours: 5.25,
    cmax: 45.0, // Estimated for oral steroid
    tmaxDays: 0.08,
    bioavailability: 0.95,
    pkModel: 'advanced',
    aromatizes: true,
    aromatizationRate: 'moderate',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'high',
    typicalDoseRangeMg: '20-50',
    recommendedFrequency: '2x/day',
    route: 'oral',
    sources: []
  },
  {
    name: 'Stanozolol (Oral)',
    shortName: 'Winstrol',
    ester: null,
    category: 'steroid',
    compoundClass: 'oral_steroid',
    halfLifeDays: 0.375,
    halfLifeHours: 9,
    cmax: 35.0, // Estimated for oral steroid
    tmaxDays: 0.06,
    bioavailability: 1.0,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'high',
    typicalDoseRangeMg: '25-50',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: []
  },
  {
    name: 'Chlorodehydromethyltestosterone',
    shortName: 'Turinabol',
    ester: null,
    category: 'steroid',
    compoundClass: 'oral_steroid',
    halfLifeDays: 0.67,
    halfLifeHours: 16,
    cmax: 30.0, // Estimated for oral steroid
    tmaxDays: 0.1,
    bioavailability: 1.0,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'moderate',
    typicalDoseRangeMg: '40-80',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/1798729/'
    ]
  },
  {
    name: 'Fluoxymesterone',
    shortName: 'Halotestin',
    ester: null,
    category: 'steroid',
    compoundClass: 'oral_steroid',
    halfLifeDays: 0.083,
    halfLifeHours: 2,
    cmax: 800.0,
    tmaxDays: 0.075,
    bioavailability: 0.57,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'high',
    typicalDoseRangeMg: '10-40',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/4009439/'
    ]
  },

  // ============================================
  // ANCILLARIES - AROMATASE INHIBITORS
  // ============================================
  {
    name: 'Anastrozole',
    shortName: 'Arimidex',
    ester: null,
    category: 'ancillary',
    compoundClass: 'ai',
    halfLifeDays: 1.95,
    halfLifeHours: 46.8,
    cmax: 3930.0,
    tmaxDays: 0.042,
    bioavailability: 0.8,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '0.25-1',
    recommendedFrequency: 'eod to 2x/week',
    route: 'oral',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/19470631/'
    ]
  },
  {
    name: 'Exemestane',
    shortName: 'Aromasin',
    ester: null,
    category: 'ancillary',
    compoundClass: 'ai',
    halfLifeDays: 0.95,
    halfLifeHours: 22.7,
    cmax: 57.6,
    tmaxDays: 0.059,
    bioavailability: 0.05,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '12.5-25',
    recommendedFrequency: 'eod',
    route: 'oral',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1884784/'
    ]
  },
  {
    name: 'Letrozole',
    shortName: 'Letro',
    ester: null,
    category: 'ancillary',
    compoundClass: 'ai',
    halfLifeDays: 1.39,
    halfLifeHours: 33.4,
    cmax: 45.684,
    tmaxDays: 0.078,
    bioavailability: null,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '0.5-2.5',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/16229115/'
    ]
  },

  // ============================================
  // ANCILLARIES - SERMS
  // ============================================
  {
    name: 'Tamoxifen',
    shortName: 'Nolvadex',
    ester: null,
    category: 'ancillary',
    compoundClass: 'serm',
    halfLifeDays: 1.975,
    halfLifeHours: 47.4,
    cmax: 200.0,
    tmaxDays: 0.344,
    bioavailability: 0.15,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '10-40',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: [
      'https://www.accessdata.fda.gov/drugsatfda_docs/nda/2002/21109_Nolvadex_biopharmr.pdf'
    ]
  },
  {
    name: 'Clomifene',
    shortName: 'Clomid',
    ester: null,
    category: 'ancillary',
    compoundClass: 'serm',
    halfLifeDays: 5.0,
    halfLifeHours: 120,
    cmax: 40.0,
    tmaxDays: 0.208,
    bioavailability: 0.95,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '25-100',
    recommendedFrequency: 'daily',
    route: 'oral',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/19033451/'
    ]
  },

  // ============================================
  // ANCILLARIES - OTHER
  // ============================================
  {
    name: 'Cabergoline',
    shortName: 'Caber',
    ester: null,
    category: 'ancillary',
    compoundClass: 'other',
    halfLifeDays: 3.58,
    halfLifeHours: 86,
    cmax: 4.03,
    tmaxDays: 0.083,
    bioavailability: 0.465,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'low',
    typicalDoseRangeMg: '0.25-1',
    recommendedFrequency: '2x/week',
    route: 'oral',
    sources: [
      'https://pubmed.ncbi.nlm.nih.gov/12844325/'
    ]
  },

  // ============================================
  // PEPTIDES
  // ============================================
  {
    name: 'Human Chorionic Gonadotropin',
    shortName: 'HCG',
    ester: null,
    category: 'peptide',
    compoundClass: 'peptide',
    halfLifeDays: 1.96,
    halfLifeHours: 47.1,
    cmax: 2072.0,
    tmaxDays: 1.0,
    bioavailability: 0.45,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '250-500 IU',
    recommendedFrequency: '2-3x/week',
    route: 'injectable',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8301557/',
      'https://pubmed.ncbi.nlm.nih.gov/12470572/'
    ]
  },
  {
    name: 'Human Growth Hormone',
    shortName: 'HGH',
    ester: null,
    category: 'peptide',
    compoundClass: 'peptide',
    halfLifeDays: 0.17,
    halfLifeHours: 4.1,
    cmax: 622.9,
    tmaxDays: 0.22,
    bioavailability: 0.63,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '2-6 IU',
    recommendedFrequency: 'daily',
    route: 'injectable',
    sources: [
      'https://www.nature.com/articles/pr198896',
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2014216/'
    ]
  },
  {
    name: 'Semaglutide (Injectable)',
    shortName: 'Ozempic',
    ester: null,
    category: 'peptide',
    compoundClass: 'peptide',
    halfLifeDays: 6.5,
    halfLifeHours: 156,
    cmax: 2879.1,
    tmaxDays: 1.25,
    bioavailability: 0.89,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '0.25-2.4',
    recommendedFrequency: '1x/week',
    route: 'injectable',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7854449/',
      'https://link.springer.com/article/10.1007/s13300-019-0581-y'
    ]
  },
  {
    name: 'Tirzepatide',
    shortName: 'Mounjaro',
    ester: null,
    category: 'peptide',
    compoundClass: 'peptide',
    halfLifeDays: 4.86,
    halfLifeHours: 116.7,
    cmax: 5826.67,
    tmaxDays: 1.5,
    bioavailability: 0.8,
    pkModel: 'advanced',
    aromatizes: false,
    aromatizationRate: 'none',
    dhtConversion: false,
    is19Nor: false,
    hepatotoxicity: 'none',
    typicalDoseRangeMg: '2.5-15',
    recommendedFrequency: '1x/week',
    route: 'injectable',
    sources: [
      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9268041/',
      'https://www.ncbi.nlm.nih.gov/books/NBK585056/'
    ]
  }
];

export default COMPOUND_SEED_DATA;
